import React from 'react';
import { t, Trans } from '@lingui/macro';

import FrequentlyAskedQuestions, {
  FaqParagraph,
} from '@rover/components/src/FrequentlyAskedQuestions';
import { Link, Paragraph } from '@rover/kibble/core';
import FirstFAQIsOpen from '@rover/react-lib/src/pages/landing/SeoLocation/components/Footer/ServiceFaqs/FirstFAQIsOpen';
import FrequentlyAskedQuestion from '@rover/react-lib/src/pages/landing/SeoLocation/components/Footer/ServiceFaqs/FrequentlyAskedQuestion';
import UnorderedList, {
  ListItem,
} from '@rover/react-lib/src/pages/landing/SeoLocation/components/Footer/ServiceFaqs/UnorderedList';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import type { CountryConfigurationUrlsLocalizedLinkUrls } from '@rover/rsdk/src/apiClient/latest/model/countryConfigurationUrlsLocalizedLinkUrls';
import { ServiceLandingEnum } from '@rover/types/src/ServiceLanding';

type PetSittingServiceFaqsProps = {
  htmlRef?: React.RefObject<HTMLElement>;
  localizedLinkUrls: CountryConfigurationUrlsLocalizedLinkUrls;
  countryConfig: CountryConfiguration;
  serviceLinks: Record<ServiceLandingEnum, string>;
};

const PetSittingServiceFaqs: React.FC<PetSittingServiceFaqsProps> = ({
  htmlRef,
  localizedLinkUrls: links,
  countryConfig,
  serviceLinks,
}) => {
  const { hideBackgroundCheckVerbiage } = countryConfig;

  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="pet-sitting-service-faqs"
      // override defaults in FrequentlyAskedQuestions
      mt="0x"
      pb="0x"
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        <Trans>Pet sitting near you</Trans>
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        <Trans>
          Discover personalized pet care with 30 or 60 minute drop-in visits from a loving pet
          sitter near you.
        </Trans>
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        <FrequentlyAskedQuestion question={t`What is expected of a pet sitter?`}>
          <>
            <FaqParagraph>
              <Trans>
                For drop-in visits, you can choose a 30- or 60-minute stay and ask your sitter to
                give your pet potty breaks, a clean litter box, food or water refills, and
                one-on-one attention. Some sitters may have specific experience, such as senior pet
                care or medication administration, on their profile under &quot;Skills&quot; or
                &quot;Pet care experience&quot;.
              </Trans>
            </FaqParagraph>
            <FaqParagraph>
              <Trans>
                If your pet needs more personalized care, you can ask sitters about their pet care
                style, skills, and qualifications, and go over your care instructions with them
                before booking.
              </Trans>
            </FaqParagraph>
            <FaqParagraph>
              <Trans>
                To set clear expectations, we recommend writing your cat or dog's care instructions
                in the Rover app.
              </Trans>
            </FaqParagraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 2 */}
        {'findingASitter' in links && (
          <FrequentlyAskedQuestion question={t`Where can I find a pet sitter near me?`}>
            <>
              <FaqParagraph>
                <Trans>
                  Finding a local pet sitter through Rover is straightforward! Enter your zip code
                  into the address box, and sitters near you will appear on map and in a list view.
                  You can also zoom in or move around the map to explore different areas. Hit
                  refresh to re-populate search results and find new or closer sitters for you to
                  consider.
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  For more details, visit <Link href={links.findingASitter}>our Help Center</Link>.
                </Trans>
              </FaqParagraph>
            </>
          </FrequentlyAskedQuestion>
        )}

        {/* 3 */}
        <FrequentlyAskedQuestion question={t`Can I find a pet sitter for while I'm at work?`}>
          <>
            <FaqParagraph>
              <Trans>
                To see which sitters are available during your work hours, select the number of
                visits per day, the days of the week, and the time slots for when you'd like a
                sitter to visit. Your search results will show sitters who can meet your schedule.
                Once you find a sitter you like, reach out to discuss service details, such as pet
                care experience or instructions. Before booking, schedule a Meet & Greet to go over
                care instructions and home entry.
              </Trans>
            </FaqParagraph>
            <FaqParagraph>
              <Trans>
                <strong>Tip:</strong> Doggy day care is another option for pet supervision while
                you're at work. Visit our{' '}
                <Link href={serviceLinks[ServiceLandingEnum.DOGGY_DAY_CARE]}>
                  doggy day care page
                </Link>{' '}
                for more information.
              </Trans>
            </FaqParagraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 4 */}
        <FrequentlyAskedQuestion question={t`Are there pet sitters near me who stay overnight?`}>
          <>
            <FaqParagraph>
              <Trans>
                Many sitters on Rover can stay overnight in your home. Pet sitters who are willing
                to stay overnight will offer house sitting as a service. Pet sitters who only offer
                drop-ins and dog walking don't typically provide overnight care.
              </Trans>
            </FaqParagraph>
            <FaqParagraph>
              <Trans>
                <strong>Tip:</strong> If overnight stays are a criteria for future bookings, look
                for sitters who also offer house sitting under the &quot;Services&quot; or
                &quot;Service &amp; Calendar&quot; sections on a sitter's profile.
              </Trans>
            </FaqParagraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 5 */}
        <FrequentlyAskedQuestion
          question={t`What pet sitting services include medication administration?`}
        >
          <FaqParagraph>
            <Trans>
              Many pet sitters on Rover include medication administration with their services. If
              included, you will find oral medication and/or injectable medication listed on a
              sitter's profile under the &quot;Skills&quot; section.
            </Trans>
          </FaqParagraph>
        </FrequentlyAskedQuestion>

        {/* 6 */}
        {'howDoIContactAPetSitterOrDogWalkerComputer' in links && (
          <FrequentlyAskedQuestion
            question={t`Where can I find pet sitters with flexible scheduling or holiday availability?`}
          >
            <>
              <FaqParagraph>
                <Trans>
                  When searching for a pet sitter, select your dates for the most accurate
                  availability. If you are not sure of the exact dates, search within a one- or
                  two-day window for flexibility. You can also check when sitters last updated their
                  calendar for confirmation. For holiday availability, Rover shows pet sitters whose
                  calendars are open during that time. Sitters who confirmed to Rover that they're
                  available for specific holidays will also have a tag that says &quot;Confirmed
                  availability for [Holiday]&quot;. Provide more details about your dates when{' '}
                  <Link href={links.howDoIContactAPetSitterOrDogWalkerComputer}>
                    contacting a sitter
                  </Link>{' '}
                  about flexible scheduling or holiday availability.
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  <strong>Tip:</strong> Sitters who specialize in drop-in visits and dog walks may
                  have more flexibility for last-minute visits than sitters who focus only on house
                  sitting or boarding. On average, house sitting and boarding services are booked
                  about 20 days in advance.
                </Trans>
              </FaqParagraph>
            </>
          </FrequentlyAskedQuestion>
        )}

        {/* 7 - Background check verbiage (North America) */}
        {!hideBackgroundCheckVerbiage && 'safetyWithLocale' in links && (
          <FrequentlyAskedQuestion question={t`Where can I find a background-checked pet sitter?`}>
            <>
              <FaqParagraph>
                <Trans>
                  In North America, all sitters joining Rover must pass a background check before
                  they are listed on Rover. You can find the type of background check the sitters
                  has passed by looking at their profile:
                </Trans>
              </FaqParagraph>
              <UnorderedList>
                <ListItem>
                  <Trans>
                    A blue badge means the sitter has passed a verified background check.
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    A gold badge means the sitter has passed an enhanced background check.
                  </Trans>
                </ListItem>
              </UnorderedList>
              <Paragraph webProps={{ maxWidth: 'none' }} mt="2x">
                <Trans>
                  For extra peace of mind, you can also read verified reviews from other pet parents
                  and check each sitter's repeat client stats. For more details, see{' '}
                  <Link href={links.safetyWithLocale}>Rover's Trust & Safety</Link> page.
                </Trans>
              </Paragraph>
            </>
          </FrequentlyAskedQuestion>
        )}

        {/* 7 - Identity verification verbiage (Europe) */}
        {hideBackgroundCheckVerbiage &&
          'sitterIdVerified' in links &&
          'safetyWithLocale' in links && (
            <FrequentlyAskedQuestion question={t`Where can I find a trusted pet sitter?`}>
              <FaqParagraph>
                <Trans>
                  In the UK and Europe, all sitters joining Rover must{' '}
                  <Link href={links.sitterIdVerified}>verify their identity</Link> before they are
                  listed on Rover. You can see confirmation of the sitter's identity verification
                  check from the green badge on their profile. For extra peace of mind, you can also
                  read verified reviews from other pet parents and check each sitter's repeat client
                  stats. For more details, see{' '}
                  <Link href={links.safetyWithLocale}>Rover's Trust & Safety</Link> page.
                </Trans>
              </FaqParagraph>
            </FrequentlyAskedQuestion>
          )}

        {/* 8 */}
        {'meetAndGreetGuide' in links && (
          <FrequentlyAskedQuestion question={t`What should I ask when hiring a pet sitter?`}>
            <>
              <FaqParagraph>
                <Trans>
                  The questions you decide to ask your pet sitter may focus on the type of care
                  experience and pet knowledge they have. Questions about how they handle daily care
                  routines and how they manage unique pet behaviors will help you determine if they
                  are a good fit for your pet. If you are considering a sitter for long-term care or
                  as your go-to pet sitter, you may also want to ask about their emergency plans and
                  preferred communication style.
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  <strong>Tip:</strong> Not sure where to start? We have a sample list of questions
                  for <Link href={links.meetAndGreetGuide}>Meet &amp; Greets</Link>, house sitting,
                  and dog boarding to help guide your conversations.
                </Trans>
              </FaqParagraph>
            </>
          </FrequentlyAskedQuestion>
        )}

        {/* 10 */}
        {'howDoIFindAPetSitterOrDogWalkerComputer' in links &&
          'whatIsTheStarSitterProgram' in links && (
            <FrequentlyAskedQuestion question={t`How do I book a trusted local pet sitter?`}>
              <FaqParagraph>
                <Trans>
                  After entering{' '}
                  <Link href={links.howDoIFindAPetSitterOrDogWalkerComputer}>
                    your location, booking details, and search filters
                  </Link>
                  , Rover will surface local pet sitters and walkers in your city and neighborhood.
                  Look for trust markers on sitter profiles, such as background checks, repeat
                  clients, verified reviews, or{' '}
                  <Link href={links.whatIsTheStarSitterProgram}>Star Sitter status</Link>. You can
                  also get details about their experience and care style on the profile. When you
                  have found a sitter, contact them through Rover and schedule a Meet &amp; Greet.
                  Meeting in person is a chance to see how they'll interact with your pet and how
                  they understand your care instructions. After a successful Meet &amp; Greet, you
                  or your sitter can confirm the booking through Rover by clicking &quot;Book It
                  Now&quot;.
                </Trans>
              </FaqParagraph>
            </FrequentlyAskedQuestion>
          )}
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default PetSittingServiceFaqs;
